import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const CATEGORIES = [
  { key: "", label: "Tout" },
  { key: "hoodie", label: "Hoodies" },
  { key: "tshirt", label: "T-Shirts" },
  { key: "cap", label: "Casquettes" },
  { key: "limited", label: "Éditions Ltd." },
];

const PAGE_SIZE = 24;

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState(params.get("q") || "");
  const [minP, setMinP] = useState(params.get("min_price") || "");
  const [maxP, setMaxP] = useState(params.get("max_price") || "");
  const [size, setSize] = useState(params.get("size") || "");
  const category = params.get("category") || "";
  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const buildParams = useCallback((skip = 0) => {
    const p = { skip, limit: PAGE_SIZE };
    const qv = params.get("q"); if (qv) p.q = qv;
    const c = params.get("category"); if (c) p.category = c;
    const mn = params.get("min_price"); if (mn) p.min_price = mn;
    const mx = params.get("max_price"); if (mx) p.max_price = mx;
    const s = params.get("size"); if (s) p.size = s;
    return p;
  }, [params]);

  const fetchInitial = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", { params: buildParams(0) });
      setProducts(data.items);
      setTotal(data.total);
      setHasMore(data.has_more);
    } finally { setLoading(false); }
  }, [buildParams]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/products", { params: buildParams(products.length) });
      setProducts((prev) => [...prev, ...data.items]);
      setHasMore(data.has_more);
    } finally {
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  }, [buildParams, products.length, hasMore]);

  useEffect(() => { fetchInitial(); }, [fetchInitial]);

  // Infinite scroll
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: "400px" });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const applyFilters = (e) => {
    e?.preventDefault();
    const next = new URLSearchParams(params);
    q ? next.set("q", q) : next.delete("q");
    minP ? next.set("min_price", minP) : next.delete("min_price");
    maxP ? next.set("max_price", maxP) : next.delete("max_price");
    size ? next.set("size", size) : next.delete("size");
    setParams(next);
  };

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    c ? next.set("category", c) : next.delete("category");
    setParams(next);
  };

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-10" data-testid="catalog-page">
      <div className="border-b border-white/10 pb-6 mb-8">
        <h1 className="font-display text-6xl md:text-8xl uppercase">Catalogue</h1>
        <div className="mt-2 text-xs uppercase tracking-widest text-white/40" data-testid="catalog-count">
          {total} produit{total > 1 ? "s" : ""}
        </div>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="space-y-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-white/40 mb-3">Catégorie</div>
            <div className="space-y-1">
              {CATEGORIES.map((c) => (
                <button key={c.key} onClick={() => setCategory(c.key)}
                  data-testid={`filter-cat-${c.key || "all"}`}
                  className={`block w-full text-left px-3 py-2 text-sm uppercase tracking-widest ${category === c.key ? "bg-[#FF3B00] text-white" : "hover:bg-white/5"}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={applyFilters} className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-white/40 mb-2">Recherche</div>
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="filter-q"
                placeholder="Pilote, produit..."
                className="w-full bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-3 py-2 text-sm" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/40 mb-2">Prix</div>
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Min" value={minP} onChange={(e) => setMinP(e.target.value)} data-testid="filter-min"
                  className="w-full bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-3 py-2 text-sm" />
                <input placeholder="Max" value={maxP} onChange={(e) => setMaxP(e.target.value)} data-testid="filter-max"
                  className="w-full bg-transparent border border-white/20 focus:border-[#FF3B00] outline-none px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-white/40 mb-2">Taille</div>
              <div className="grid grid-cols-4 gap-1">
                {["", "S", "M", "L", "XL"].map((s) => (
                  <button type="button" key={s || "any"} onClick={() => setSize(s)}
                    className={`py-2 text-xs uppercase border ${size === s ? "bg-[#FF3B00] border-[#FF3B00]" : "border-white/20 hover:border-white"}`}>
                    {s || "Any"}
                  </button>
                ))}
              </div>
            </div>
            <button type="submit" data-testid="filter-apply" className="w-full py-3 bg-white text-black uppercase tracking-widest text-sm hover:bg-[#F2F2F2]">Appliquer</button>
          </form>
        </aside>

        <div>
          {loading ? (
            <div className="text-center py-20 text-white/50 uppercase tracking-widest">Chargement...</div>
          ) : products.length === 0 ? (
            <div className="border border-white/10 p-16 text-center text-white/50">Aucun produit trouvé.</div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {products.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
              {hasMore && (
                <div ref={sentinelRef} className="mt-8 flex justify-center">
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    data-testid="catalog-load-more"
                    className="px-8 py-4 border border-white/20 hover:border-[#FF3B00] hover:text-[#FF3B00] uppercase tracking-widest text-sm disabled:opacity-50">
                    {loadingMore ? "Chargement..." : `Charger plus (${products.length} / ${total})`}
                  </button>
                </div>
              )}
              {!hasMore && products.length >= PAGE_SIZE && (
                <div className="mt-8 text-center text-xs uppercase tracking-widest text-white/30">— Fin du catalogue —</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
